const React = require("react");
const ReactDOM = require('react-dom')
const FieldWrapper = require("components/fields/layouts/FieldWrapper");
const shortid = require("shortid");

const PropTypes = require("prop-types");

const debounce = require("helpers/debounce");
const Enum = require('helpers/Enum');

const Element = require("components/abstract/Element");

const {computed, observe, isObservable} = require("mobx");

const Injector = require("mt-react-core/decorators/Injector");

class Field extends Element {


    /////////////////////////////////
    // Construtor
    /////////////////////////////////

    constructor(props) {
        super(props);

        this.state = {
            data_uuid: shortid.generate(),
            cur_data_val: null,
            current_value: '',
            ...this.defaultState
        };

        this.field_ref = React.createRef();
        this.default_id = ['_', shortid.generate()].join('_');
        if (isObservable(props.data) && props.data) {
            observe(props.data, change => {
                if (`${change.name}` === `${this.firstPartOfName}`) {
                    this.triggerDataChange();
                }
            })
        }

    }

    /////////////////////////////////
    // Statics
    /////////////////////////////////

    static get propTypes() {
        let props = {};
        return props;
    }

    static get defaultProps() {
        const props = {
            collectOn: 'change',
            ignorePrefix: false,
        };
        return props;
    }

    get defaultState() {
        return {};
    }

    /////////////////////////////////
    // Control
    /////////////////////////////////

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.data !== this.props.data || prevProps.value !== this.props.value || prevProps.defaultValue !== this.props.defaultValue) {
            this.triggerDataChange();
        }
        if(prevState.data_uuid !== this.state.data_uuid){
            this.dataUuidUpdated();
        }

        this.focusField();
    }

    componentDidMount() {

        if(this.props.refreshOnStoreUpdate === true && this.props.store){
            this.props.store.collectionChange.pipe(collection => {

                this.store_refresh_timeout = setTimeout(() => {
                    this.triggerDataChange();
                    this.store_refresh_timeout = null;
                }, 0);

                return collection;
            });
        }

        this.setState({current_value: this.value}, () => {
            this._onBoot();
        });

        this.focusField();
    }

    focusField() {
        if (!this.props.autoFocus) return;

        const {current} = this.field_ref;

        if (current && typeof current.focus === 'function') {
            current.focus();
        }
    }

    /////////////////////////////////
    // Error helpers
    /////////////////////////////////

    get hasErrors() {
        return this.props.errors !== null && typeof(this.props.errors) !== 'undefined';
    }

    get hasError() {
        return this.error !== null;
    }

    get errors() {
        if (this.hasErrors) {
            return this.props.errors;
        }
        return {};
    }

    get error() {
        let dottedName = this.splitName(this.as).join('.');

        let error = null;

        if (typeof this.errors.has === 'function') {
            error = this.errors.get(dottedName);
        } else {
            error = this.errors[dottedName];
        }

        if (error && error.length && error.length > 0) {
            return error.join(' ')
        }

        return null;
    }

    /////////////////////////////////
    // Prop helpers
    /////////////////////////////////

    resolveProp = (propName, defaultValue = null) => {

        if (typeof(this.props[propName]) !== 'undefined') {

            if (typeof(this.props[propName]) === 'function') {
                if (this.props.data === null || typeof(this.props.data) === 'undefined') {
                    return null;
                }
                return this.props[propName](this.props.data, this);
            }

            return this.props[propName];
        }

        return (defaultValue === null && this.defaultProps && !!this.defaultProps[propName]) ?
            this.defaultProps[propName] :
            defaultValue;
    }

    get min() {
        return this.resolveProp('min', null);
    }

    get max() {
        return this.resolveProp('max', null);
    }

    get defaultValue() {
        return this.resolveProp('defaultValue', null);
    }

    get step() {
        return this.resolveProp('step', null);
    }

    get disabled() {
        return this.resolveProp('disabled', false);
    }

    get disabledOnEdit() {
        return this.resolveProp('disabledOnEdit', false);
    }

    get isDisabled() {
        return this.disabled === true || (this.disabledOnEdit && this.hasData() && this.getDataValue('id'));
    }

    get isReadonly() {
        return this.props.readOnly === true;
    }

    /////////////////////////////////
    // Callbacks
    /////////////////////////////////

    changeCallbacks = [];

    enqueueChangeCallback(callback) {
        this.changeCallbacks.push(callback);
    }

    triggerChangeCallbacks(val) {
        if (this.changeCallbacks.length > 0) {
            this.changeCallbacks.forEach(callback => {
                if (typeof(callback) === 'function') {
                    callback(val);
                }
            });
        }
    }

    /////////////////////////////////
    // Change events
    /////////////////////////////////

    _onBoot(value) {
        if (this.props.onBoot && typeof this.props.onBoot === "function") {
            this.props.onBoot(this.value, this);
        }
    }

    modifyOnChangeValue(value) {
        return value;
    }

    onChange(value) {

        if (this.props.onChange && typeof this.props.onChange === "function") {
            this.props.onChange(value, this);
        }

        if ((this.props.collectOn === 'change' || !this.props.collectOn) && this.props.collector && typeof(this.props.collector.updateValue) === 'function') {
            this.props.collector.updateValue(this.name, value);
        }

        this.triggerChangeCallbacks(value);
    }

    onChangeDebounced = debounce(200, (value) => {
        this.onChange(value);
    });

    _onChange = (e) => {

        let value = (e.target) ? e.target.value : e;

        value = this.modifyOnChangeValue(value);

        this.setState({current_value: value}, () => {
            if (this.props.debounce === true) {
                this.onChangeDebounced(value);
            } else {
                this.onChange(value);
            }
        });

    }

    onBlur(value) {

        if (this.props.onBlur && typeof this.props.onBlur === "function") {
            return this.props.onBlur(value, this);
        }

        if (this.props.collectOn === 'blur' && this.props.collector && typeof(this.props.collector.updateValue) === 'function') {
            this.props.collector.updateValue(this.name, value);
        }
    }

    onBlurDebounced = debounce(200, (value) => {
        this.onBlur(value);
    });

    _onBlur(e) {

        let value = (e.target) ? e.target.value : e;

        if (this.props.debounce === true) {
            this.onBlurDebounced(value);
        } else {
            this.onBlur(value);
        }

    }

    /////////////////////////////////
    // Generic helpers
    /////////////////////////////////

    flatten(value) {

        if (typeof(value) === 'string') {
            return value;
        }

        if (Array.isArray(value)) {
            return value.join('_');
        }

    }

    get options() {

        if (Array.isArray(this.props.options) && this.props.options.length > 0) {
            return this.props.options;
        }

        if (typeof(this.props.enum) !== 'undefined') {
            return (new Enum(this.props.enum)).options;
        }

        if (this.props.store && this.props.store.collection) {
            if (this.props.optionsKey) {
                return this.props.store.collection.map((val, index) => {
                    return {
                        value: val.id,
                        label: val[this.props.optionsKey]
                    };
                });
            } else {
                return this.props.store.collection;
            }
        }

        return [];
    }

    get hasCurrentFieldFocus(){
        if(!this.field_ref || !this.field_ref.current){
            return false;
        }
        return document.activeElement === ReactDOM.findDOMNode(this.field_ref.current);
    }

    /////////////////////////////////
    // Data helpers
    /////////////////////////////////

    dataUuidUpdated() {

    }

    updateDataUuid(other_states = {}) {

        const {cur_data_val} = this.state;
        const cur_data = this.getDataValue(this.name);

        let obj = {
            current_value: this.value,
        };

        if (this.state.current_value != this.value) {

            if(this.hasCurrentFieldFocus === false) {
                obj.data_uuid = shortid.generate();

                if (cur_data_val !== cur_data) {
                    //obj.data_uuid = shortid.generate();
                } else {
                    //delete obj.current_value;
                }
            } else {

            }
        }

        this.setState({...obj, cur_data_val: cur_data, ...other_states});
    }

    injectCurrentValue(value){
        if(this.field_ref && this.field_ref.current){
            this.field_ref.current.value = value;
            this.setState({current_value: value});
        }
    }

    triggerDataChange(other_states = {}) {
        this.updateDataUuid(other_states);
    }

    hasData() {
        return this.props.data !== null && typeof(this.props.data) !== 'undefined';
    }

    searchObject(object, query, defaults = null) {

        if (typeof(query) === 'string') {
            query = query.split('.');
        }

        const res = query.reduce((o, i) => o ? o[i] : undefined, object);

        return (typeof res !== 'undefined') ? res : defaults;
    }

    getDataValue(name) {

        if (typeof(name) === 'undefined') {
            return null;
        }

        const xah_map_to_obj = (aMap => {
            const obj = {};
            aMap.forEach((v, k) => {
                obj[k] = v
            });
            return obj;
        });

        let data = this.props.data;

        if (typeof(this.props.data.get) === 'function') {
            data = xah_map_to_obj(data);
        }

        //let value = this.props.data[this.name];
        let value = this.searchObject(data, this.splitName(name));

        return value;
    }

    getMyDataValue() {

        let value = this.getDataValue(this.name);

        if (typeof(value) !== 'undefined' && value !== null) {
            return value;
        }

        return '';
    }

    /////////////////////////////////
    // Base getters
    /////////////////////////////////

    splitName(name) {
        if (typeof(name) !== 'string') {
            return [];
        }
        return name.split(/[\[\]\.]/).filter(part => part !== "");
    }

    get firstPartOfName() {
        return this.splitName(this.name)[0];
    }

    resolveName(name) {
        let parts = [];

        if (typeof(this.props.prefix) !== 'undefined' && !this.props.ignorePrefix) {

            let prefix = this.props.prefix;

            if (typeof(prefix) === 'object') {
                prefix = '';
            }

            if (typeof(prefix) !== 'string') {
                prefix = prefix + "";
            }

            if (prefix !== '') {
                parts.push(...this.splitName(prefix));
            }
        }

        if (typeof(name) !== 'undefined') {
            parts.push(...this.splitName(name));
        }

        if (typeof(this.props.suffix) !== null && typeof(this.props.suffix) !== 'undefined') {

            let suffix = this.props.suffix;

            if (typeof(suffix) === 'object') {
                suffix = '';
            }

            if (typeof(suffix) !== 'string') {
                suffix = suffix + "";
            }

            if (suffix !== '') {
                parts.push(...this.splitName(suffix));
            }
        }

        let new_name = parts.shift();

        if (parts.length > 0) {
            new_name = new_name + "[" + parts.join("][") + "]";
        }

        return new_name;
    }

    get name() {
        return this.resolveName(this.props.name);
    }


    get as() {

        if (this.props.as) {
            return this.props.as;
        }

        return this.name;
    }

    findValue() {

        // If a specific value prop has been supplied,
        // we are likely looking at an attempt of controlling the field,
        // thus, we will simply use the supplied value
        if (typeof (this.props.value) !== 'undefined') {
            return this.props.value;
        }

        // Like with the controlled value field, use checked props if set
        // Mainly used for controlled checkboxes
        if (this.props.checked) {
            return this.props.checked;
        }

        // Has data been provided to the field?
        // And is there a value matching the name of the field?
        // Then use the value from data
        if (this.hasData()) {
            let value = this.getMyDataValue();
            if (value !== null && typeof(value) !== 'undefined' && value !== '') {
                return value;
            }
        }

        // In case a default value is supplied, use that one
        if (this.defaultValue) {
            return this.defaultValue;
        }

        // In case a default checked value is supplied, use that one
        if (this.props.defaultChecked) {
            return this.props.defaultChecked;
        }

        // Return an empty string to use as a default value
        return '';
    }

    get value() {

        let value = this.findValue();

        if (typeof value === 'number' && isNaN(value)) {
            value = '';
        }

        if (this.props.autoCollect === true && this.props.collector && typeof(this.props.collector.setAutocollect) === 'function') {
            this.props.collector.setAutocollect(this.as, this);
        }

        return value;
    }

    get label() {

        if (!this.props.label) {
            return null;
        }

        return (
            <React.Fragment >
                <span >{this.resolveProp('label')}</span >
                {this.renderRequired()}
            </React.Fragment >
        );
    }

    get placeholder() {
        return this.resolveProp('placeholder', '');
    }

    get id() {

        if (this.props.id) {
            return this.props.id;
        }

        return this.default_id;
    }

    get isRequired() {
        return !this.props.required;
    }

    /////////////////////////////////
    // Advanced getters
    /////////////////////////////////

    get field_key() {

        // To handle rerender of field if external data changes,
        // this key will update whenever data or default value changes

        return [
            'field', //A generic prefix
            this.flatten(this.name), //Scope to the current field (name should generally be unique
            this.state.data_uuid //
        ].join('_');

    }

    /////////////////////////////////
    // Renderers
    /////////////////////////////////

    renderField() {
        //
    }

    renderRequired() {
        if (!this.props.required) return null;
        return (<abbr className={'is-required'} >*</abbr >);
    }

    renderErrors() {
        if (!this.hasError) return null;

        return (<p className="field-error" >{this.error}</p >);
    }

    renderFieldWrapper() {
        return (
            <FieldWrapper label={this.label} placeholder={this.placeholder} fieldId={this.id} hasError={this.hasError} >
                {this.renderInnerField()}
            </FieldWrapper >
        );
    }

    renderInnerField() {
        return (
            <React.Fragment >
                <React.Fragment key={this.field_key} >
                    {this.renderField()}
                </React.Fragment >
                {this.renderErrors()}
                {this.props.children}
            </React.Fragment >
        );
    }

    render() {

        if (this.props.naked === true) {
            return this.renderField();
        }

        return this.renderFieldWrapper();
    }

    /////////////////////////////////
    // Wrapped
    /////////////////////////////////

    static inject(extra_stores = []) {
        let stores = ["data", "errors", "prefix", "suffix", "store", "collector", ...extra_stores];
        return Injector(stores)(this);
    }

}

module.exports = Field;
